import { Link, useSearchParams } from 'react-router-dom'
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react'
import {
  activeConversations,
  activeDiscourses,
  activeAssistants,
  activeGames,
  activeLearning,
  activeScenarios,
  copyFor,
  copyForConversation,
  hrefFor,
  hrefForConversation,
  type Conversation,
  type Scenario,
} from '../../content/scenarios'
import { companiesOn, entityFor, hrefForCompany, type Company } from '../../content/companies'
import { useAuth } from '../../lib/auth'
import { useLocale, useT, type MessageKey } from '../../lib/i18n'
import LocalePicker from '../../components/LocalePicker'
import Wordmark from '../../components/Wordmark'
import ThemePicker from '../../components/ThemePicker'
import SeatPicker from '../../components/SeatPicker'
import styles from './Selector.module.css'

/**
 * The member front page: what you may go into.
 *
 * One flat list. The worlds/scenarios shelving is gone until there is enough on
 * the shelf to need it — a section label over a single card is furniture, not
 * structure.
 *
 * ── THE SECOND SHELF (2026-08-25 — the Talk-to-X wedge, Tier 0) ──────────────
 * The principle above is kept EXACTLY as written and is now doing real work rather than
 * describing an absence: the sections are computed, empty ones are dropped, and the headings
 * appear only when more than one survives. So the page a reader has been seeing — a wordmark
 * over one unlabelled grid — is byte-identical until the day a second kind of thing is on the
 * shelf, which is today.
 *
 * WHY GROUP AT ALL, when the file's instinct is not to: the two lists differ in KIND, not in
 * title. A crossing into a place and a ten-minute conversation with somebody who will not budge
 * are two different offers, and mixing them in one grid asks the reader to work that out from the
 * blurbs. That is the one thing a section heading is genuinely for.
 *
 * ── AND THEY ARE RENDERED DIFFERENTLY, WHICH IS THE POINT (the shelf split, same evening) ────
 * WORLDS keep their cards, blurb and all: an hours-long door earns a description, and nothing
 * about them changed. CONVERSATIONS are a compact LIST — title, one line, whole row tappable —
 * because the offer is small enough to state in a line and because each row is its own address
 * (`hrefForConversation`), which is what lets a reader open the one thing they saw somebody else
 * play. Two kinds of thing that looked identical on a shelf would be the shelf lying.
 */

/** ⚠ Placeholder copy, the founder's pen — see the keys in lib/i18n.tsx. */
const HEADING: Record<
  'worlds' | 'conversations' | 'discourses' | 'learning' | 'assistants' | 'games',
  MessageKey
> = {
  worlds: 'shelf.sectionWorlds',
  conversations: 'shelf.sectionChats',
  discourses: 'shelf.sectionDiscourses',
  learning: 'shelf.sectionLearning',
  assistants: 'shelf.sectionAssistants',
  games: 'shelf.sectionGames',
}

function Card({ s, index }: { s: Scenario; index: number }) {
  const { locale } = useLocale()
  const t = useT()
  const copy = copyFor(s, locale)
  /** Absent means enterable — see `Scenario.enterable`. */
  const held = s.enterable === false
  const style = {
    '--accent': s.accent,
    animationDelay: `${index * 90}ms`,
  } as CSSProperties

  const face = (
    <>
      <h2 className={styles.cardTitle}>{copy.title}</h2>
      <p className={styles.role}>{copy.role}</p>
      {/*
        ⚠ THE BLURB IS OFF THE CARD (founder, 2026-09-02: "we skip the descriptions inside each
        card, at least for the moment"). It is a pause, not a deletion — `copy.blurb` is still
        authored and still resolves per locale, so putting it back is uncommenting one line.
        What the card says now is the title, the role and the tags: who this is and what kind of
        thing it is, with the choosing left to the tab above it.
      */}
      {/*
        Tags as one quiet line rather than pills, and no ENTER affordance: the
        card IS the link — it lifts on hover and the cursor says so, and on touch
        a card on a shelf is self-evidently tappable. ENTER was invisible at rest
        on pointer devices while still reserving ~70px, which truncated the tags
        at 375px. Invisible furniture should not squeeze out real information.
      */}
      <div className={styles.foot}>
        <span className={styles.tags}>{copy.tags.join(' · ')}</span>
        {/*
          The one word a held card adds, and it sits where ENTER would have — the
          affordance's own corner, saying why there isn't one. Grey alone reads as
          broken; grey plus this reads as not yet.
        */}
        {held && <span className={styles.soon}>{t('shelf.notYet')}</span>}
      </div>
    </>
  )

  /*
   * A HELD CARD IS NOT A LINK, rather than a link that refuses.
   *
   * `<a>` with a click handler that cancels is the shape that lies: it keeps the cursor, the
   * status-bar URL, the middle-click, the long-press "open in new tab" and the keyboard tab-stop,
   * all of them promising a door. Not rendering the anchor is the honest version and needs no
   * `preventDefault`, no `aria-disabled`, no `tabIndex: -1` — there is nothing to disable.
   */
  if (held) {
    return (
      <div className={`${styles.card} ${styles.held}`} style={style}>
        {face}
      </div>
    )
  }

  return (
    <Link to={hrefFor(s)} className={styles.card} style={style}>
      {face}
    </Link>
  )
}

/**
 * A CONVERSATION — one row, and the whole row is the door.
 *
 * It deliberately borrows the corridor's vocabulary rather than the card's: a name, one line
 * under it, a hairline, nothing else. What is absent is the design, exactly as one level in —
 * no accent, no tags, no duration, no count of anything. The hook says the situation and the row
 * opens it.
 *
 * ⚠ THE ROW AND ITS ADDRESS ARE THE SAME OBJECT — the social-object law: what a reader watched
 * somebody else play has to be a link they can follow. Since pack-per-conversation (2026-08-25)
 * that address is a bare `/p/<pack>`: the conversation IS the pack, one persona in it, and
 * `surfaces/messaging` enters the single thread without a corridor in between.
 */
function Row({ c, index }: { c: Conversation; index: number }) {
  const { locale } = useLocale()
  const copy = copyForConversation(c, locale)
  return (
    <HeldRow held={c.enterable === false} to={hrefForConversation(c)} index={index}>
      <span className={styles.rowTitle}>{copy.title}</span>
      <span className={styles.hook}>{copy.hook}</span>
    </HeldRow>
  )
}

/**
 * A ROW THAT MAY BE HELD — the row's version of the card's `.held` state, and the same three
 * rules: it is greyed, it is **not a link**, and it says why.
 *
 * ⚠ THE LABEL SITS ON THE TITLE LINE, not in a corner. A card has a foot — the place ENTER would
 * have been — and a row has no such place, so the announcement rides beside the name, where the
 * eye already is. Same word, same weight, different anchor because the object is a different shape.
 *
 * ⚠ AND IT IS A `div`, NOT AN ANCHOR THAT REFUSES. Same reasoning as the card (`Card` above): an
 * `<a>` with a cancelled click keeps the cursor, the status-bar URL, middle-click, long-press
 * "open in new tab" and the keyboard tab-stop — five promises of a door. Not rendering the anchor
 * needs no `preventDefault`, no `aria-disabled`, no `tabIndex: -1`, because there is nothing to
 * disable.
 */
function HeldRow({
  held,
  to,
  index,
  children,
}: {
  held: boolean
  to: string
  index: number
  children: ReactNode
}) {
  const t = useT()
  const style = { animationDelay: `${index * 90}ms` } as CSSProperties

  if (!held) {
    return (
      <Link to={to} className={styles.row} style={style}>
        {children}
      </Link>
    )
  }

  return (
    <div className={`${styles.row} ${styles.heldRow}`} style={style}>
      <span className={styles.soonRow}>{t('shelf.notYet')}</span>
      {children}
    </div>
  )
}

/**
 * A COMPANY — a card, smaller than a world's (founder-ruled 2026-08-27).
 *
 * ⚠ THIS REVERSES THE ROW, and the reason is that the thing the row protected has left the shelf.
 * The note that stood here argued: *"a company and a conversation are different things behind the
 * row, but on the shelf they are the same offer at the same size… giving the company a card would
 * say it is an hours-long crossing, which is what a card means here."* That was true while
 * conversation rows sat beside them. They are all `active: false` now — the company pages became
 * the way in — so **the only rows left ARE companies**, and row-versus-card stopped distinguishing
 * *offer size* and started distinguishing *company from world*, which is a different axis and reads
 * as arbitrary.
 *
 * ⚠ SMALLER, AND WITHOUT AN ACCENT BAR. Same material as a world card — panel, hairline, the same
 * arrival — at less of it, because the offer is smaller and the shelf should still say so without
 * a heading having to. The missing accent is honest rather than decorative: a world declares one
 * per pack, a company declares none, and inventing a colour to fill the slot would be the card
 * claiming a property the thing does not have.
 *
 * ⚠ BOTH GENRES TAKE THE SAME FORM (founder, 2026-08-27: *"its fine to keep the different genres
 * under the same form category"*). A marketplace with listings and a roster of people render
 * identically here; the difference is what the page behind is made of, which is the page's business
 * and not the shelf's.
 *
 * ⚠ ITS NAME IS PER-LOCALE AND ITS HOOK IS NOT — see `companies.ts`. The company trades under a
 * national entity in each market (founder-ruled 2026-08-26), so the title resolves through
 * `entityFor`; everything else here, and every word on the page behind it, is placeholder English.
 * THE FULL LEGAL NAME (founder-ruled 2026-08-27): the shelf speaks ABOUT the company, and a listing
 * names a company formally — *Colloquy Services Ltd.* on the informal shelf is the joke doing its
 * own work. The company's own masthead still takes the brand alone (`Company.tsx`).
 */
function CompanyCard({ c, index }: { c: Company; index: number }) {
  const { locale } = useLocale()
  const t = useT()
  const entity = entityFor(c, locale)
  const held = c.enterable === false
  const style = { animationDelay: `${index * 90}ms` } as CSSProperties

  const face = (
    <>
      <h2 className={styles.companyTitle}>{`${entity.brand} ${entity.suffix}`}</h2>
      {/* ⚠ Same pause as the world card's blurb — `c.shelfHook` is still authored and still
          rendered on the company's own page; it is only off the shelf tile. */}
      {/* The held word sits where a world card puts it — the foot, in the corner an affordance
          would have used. A card has that corner; the row it replaced did not. */}
      {held && (
        <div className={styles.foot}>
          <span className={styles.soon}>{t('shelf.notYet')}</span>
        </div>
      )}
    </>
  )

  // A held card is a div, never an anchor that refuses — see `Card` for the five promises an
  // `<a>` keeps making after its click has been cancelled.
  if (held) {
    return (
      <div className={`${styles.companyCard} ${styles.held}`} style={style}>
        {face}
      </div>
    )
  }

  return (
    <Link to={hrefForCompany(c)} className={styles.companyCard} style={style}>
      {face}
    </Link>
  )
}

export default function Selector() {
  const { isAdmin, signOut } = useAuth()
  const t = useT()
  const worlds = activeScenarios()
  const conversations = activeConversations()
  const discourses = activeDiscourses()
  const learning = activeLearning()
  const assistants = activeAssistants()
  const games = activeGames()
  // Two shelves, two lists. A company is the same FORM on either; the heading above it is what
  // says which register the reader is being offered — see `Company.shelf`.
  const companies = companiesOn('chats')
  const houses = companiesOn('discourses')
  const schools = companiesOn('learning')

  /*
   * The stagger runs across the WHOLE page and not per section, so the shelf still arrives as one
   * thing being set down rather than as two lists arriving separately. Hence the running index
   * rather than each group's own — the rows carry on counting where the cards left off.
   */
  /*
   * ⚠ THE STAGGER IS PER PANEL NOW. It used to run across the whole page so the shelf arrived as
   * one thing being set down — but with one panel visible at a time, a running count would give
   * the last tab a delay measured from cards nobody can see, and its first card would land a
   * second late. Each container counts its own; a section holding both cards and rows offsets
   * the rows by the cards above them, so the two still read as one arrival.
   */
  const sections: { key: keyof typeof HEADING; body: ReactNode }[] = []

  /*
   * ⚠ CONVERSATIONS FIRST, WORLDS UNDER THEM (founder-ruled 2026-08-25: "easier onboarding").
   *
   * The shelf reads SHALLOW TO DEEP. A ten-minute conversation is the smallest complete unit of
   * pleasure on this engine and asks a newcomer for nothing but a first line; a world is an
   * hours-long sitting and asks for the afternoon. Putting the large offer first makes the page
   * open with the biggest ask, which is the wrong first question to put to somebody who has just
   * been let in.
   *
   * ⚠ ORDER OF PUSH IS ORDER ON THE PAGE, and the stagger runs straight through it — the rows
   * arrive first and the cards carry on counting, so the shelf still lands as one thing being set
   * down. Nothing else about either section moved.
   */
  /*
   * ⚠ COMPANIES AND CONVERSATIONS SHARE THE SECTION, and companies come first (2026-08-26).
   *
   * A CHATS row is a promise about tone and shape, not about what is behind it: some of these
   * people work for somebody, and the ones who do are reached through their employer's page. A
   * second section for companies would be the shelf explaining its own filing system.
   *
   * Companies first because a company row leads to several conversations and a conversation row
   * leads to one, and shallow-to-deep is the order this page already reads in. One list, one
   * running stagger, one hairline rhythm — `.rows` never learns there are two kinds in it.
   *
   * ⚠ TODAY THERE ARE NO ACTIVE CONVERSATION ROWS: both live packs are behind the company page,
   * and their own addresses still work (see the note over CONVERSATIONS). The branch stays because
   * the next Chat that works for nobody is a row of its own.
   */
  if (companies.length + conversations.length > 0) {
    sections.push({
      key: 'conversations',
      body: (
        /*
         * TWO FORMS IN ONE SECTION, and each gets its own container.
         *
         * Companies are cards now and conversations are still rows, so they cannot share a wrapper:
         * a grid stacks rows badly and a list lays cards out in a column. Both guards are real —
         * every conversation row is `active: false` today, so the second block renders nothing
         * until one comes back, and the section is a grid of companies in the meantime.
         */
        <>
          {companies.length > 0 && (
            <div className={styles.companyGrid}>
              {companies.map((c, i) => (
                <CompanyCard key={c.id} c={c} index={i} />
              ))}
            </div>
          )}
          {conversations.length > 0 && (
            <div className={styles.rows}>
              {conversations.map((c, i) => (
                <Row key={c.id} c={c} index={companies.length + i} />
              ))}
            </div>
          )}
        </>
      ),
    })
  }

  /*
   * ⚠ DISCOURSES, BETWEEN THE TWO (founder-commissioned 2026-08-26). The page still reads
   * SHALLOW TO DEEP and this is the middle rung: a Discourse asks a newcomer for a first line,
   * exactly as a Chat does, but it has no end — you leave when you leave — where a Chat is one
   * framed scene you can finish on a lunch break, and a world is an afternoon.
   *
   * ⚠ IT IS ITS OWN SECTION AND NOT MORE ROWS UNDER CHATS, which is the only thing about this
   * that was a real decision. The rows render identically (see `content/scenarios.ts`), so the
   * heading is carrying the entire distinction — and the distinction is TONE. CHATS promises
   * something light by definition; a reader who taps a CHATS row must never land in earnest.
   * These are earnest. Putting them in that section would make the section's promise a lie, and
   * a section that promises nothing reliable is furniture.
   */
  if (discourses.length > 0 || houses.length > 0) {
    sections.push({
      key: 'discourses',
      body: (
        // The same two containers as CHATS, and for the same reason: cards and rows cannot share a
        // wrapper. Since the Academy took Ivor inside, the rows block renders nothing — kept
        // guarded so a standalone discourse coming back needs no change here.
        <>
          {houses.length > 0 && (
            <div className={styles.companyGrid}>
              {houses.map((c, i) => (
                <CompanyCard key={c.id} c={c} index={i} />
              ))}
            </div>
          )}
          {discourses.length > 0 && (
            <div className={styles.rows}>
              {discourses.map((d, i) => (
                <Row key={d.id} c={d} index={houses.length + i} />
              ))}
            </div>
          )}
        </>
      ),
    })
  }

  /*
   * ⚠ LEARNING, BETWEEN DISCOURSES AND WORLDS (founder-commissioned 2026-08-28 — "we need to
   * start off with the language learning pack"). Shallow-to-deep still orders the page: a lesson
   * asks a newcomer for a first line, like a Chat and a Discourse; unlike either, it is the start
   * of a SEASON — the deepest ongoing ask of the row shelves, so it stands last among them,
   * before the afternoon-sized Worlds. ⚠ Order is the builder's read of the existing rule, not a
   * ruling — the founder may want LEARNING higher.
   *
   * ⚠ THE SCHOOL CARD IS THE DOOR (founder-ruled 2026-08-28, same day, correcting a shortcut
   * that put Marta's row here directly for about an hour): the shelf shows the school
   * (`companiesOn('learning')`), its page lists LANGUAGES, and each language opens its teacher —
   * you pick the language first; the teacher is who it comes with. Same two-container shape as
   * CHATS and DISCOURSES; the rows block is guarded and empty today, kept for a teacher who
   * someday works for nobody.
   */
  if (learning.length > 0 || schools.length > 0) {
    sections.push({
      key: 'learning',
      body: (
        <>
          {schools.length > 0 && (
            <div className={styles.companyGrid}>
              {schools.map((c, i) => (
                <CompanyCard key={c.id} c={c} index={i} />
              ))}
            </div>
          )}
          {learning.length > 0 && (
            <div className={styles.rows}>
              {learning.map((l, i) => (
                <Row key={l.id} c={l} index={schools.length + i} />
              ))}
            </div>
          )}
        </>
      ),
    })
  }

  /*
   * ⚠ ASSISTANTS, BETWEEN LEARNING AND GAMES (founder-commissioned 2026-09-02: "as a selectable
   * pack on the front page"). Shallow-to-deep places it here, as the builder's read: a standing
   * arrangement about your own day is the deepest ongoing ask of the row shelves — deeper than a
   * season of lessons, because it has no term. The founder may want it higher. Rows only, no
   * company card, on the Indriði rule that one person is a row; the heading is his pen
   * (`shelf.sectionAssistants`).
   */
  if (assistants.length > 0) {
    sections.push({
      key: 'assistants',
      body: (
        <div className={styles.rows}>
          {assistants.map((a, i) => (
            <Row key={a.id} c={a} index={i} />
          ))}
        </div>
      ),
    })
  }

  /*
   * ⚠ GAMES ABOVE WORLDS (founder-ruled 2026-09-01, correcting the builder's first order the same
   * evening: "move Hildur to the bottom").
   *
   * The builder had put GAMES last on the shallow-to-deep rule — a game asks for the afternoon AND
   * can send the reader away having failed, so it read as the largest ask. The ruling reverses it,
   * and the shelf's own logic supports the reversal better: WORLDS now holds exactly one entry, a
   * held prototype with nothing at stake, which is the *quietest* thing on the page rather than the
   * deepest. So GAMES is the last real OFFER and WORLDS is the footer under it. Depth-of-ask still
   * orders everything above.
   *
   * ⚠ IDENTICAL RENDERING TO WORLDS, on purpose — same `Card`, same grid. These are the same
   * OBJECT and the heading carries the entire distinction, exactly as it does between CHATS and
   * DISCOURSES. What GAMES promises is STAKES, not a shape: something to want, the possibility of
   * failing to get it, an evening that asks something of you (`content/scenarios.ts`, the GAMES
   * header). Hildur stays under WORLDS because she has nothing to lose, and a heading that promised
   * consequence over her would be the section lying.
   */
  if (games.length > 0) {
    sections.push({
      key: 'games',
      body: (
        <div className={styles.grid}>
          {games.map((s, i) => (
            <Card key={s.id} s={s} index={i} />
          ))}
        </div>
      ),
    })
  }

  if (worlds.length > 0) {
    sections.push({
      key: 'worlds',
      body: (
        <div className={styles.grid}>
          {worlds.map((s, i) => (
            <Card key={s.id} s={s} index={i} />
          ))}
        </div>
      ),
    })
  }

  /*
   * ── THE SHELVES BECAME TABS (founder-commissioned 2026-09-02) ────────────────────────────
   *
   * The page used to stack every shelf vertically with a label over each. That was right while
   * there were two; at five it is a long scroll on a phone where the thing you came for is
   * usually below the fold, and the headings had become signposts you scroll PAST rather than
   * choose between.
   *
   * ⚠ THE NO-FURNITURE RULE IS UNCHANGED AND STILL DOES THE WORK — it has only moved from
   * headings to the tab bar. One surviving section renders exactly as it always has: no bar, no
   * label, a bare grid under the wordmark. The bar appears at two, which is the same condition
   * `headed` expressed.
   *
   * WHY THE URL AND NOT LOCAL STATE: a reader taps into a pack and comes back, and losing their
   * shelf on every return is the kind of small rudeness that makes a page feel careless. It also
   * makes a shelf linkable. `replace` keeps the back button meaning "leave the shelf" rather
   * than "undo my last tab", which is what a reader expects of a filter.
   */
  const [params, setParams] = useSearchParams()
  const tabbed = sections.length > 1
  const requested = params.get('shelf')
  const activeIndex = Math.max(0, sections.findIndex((s) => s.key === requested))
  const active = sections[activeIndex]

  const select = (key: string) => {
    const next = new URLSearchParams(params)
    next.set('shelf', key)
    setParams(next, { replace: true })
  }

  /* Arrow keys move between tabs, which is what the tablist pattern promises the moment the
     roles are declared. Roving tabIndex below keeps the bar ONE tab stop, not five. */
  const onTabKey = (e: KeyboardEvent<HTMLElement>) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!step) return
    e.preventDefault()
    select(sections[(activeIndex + step + sections.length) % sections.length].key)
  }

  return (
    <div className={styles.page}>
      <LocalePicker />
      {/*
        No heading. A wordmark above a grid of cards is self-evidently a shelf;
        naming it would be the page explaining itself, which is the thing this
        product does not do. "Ways In" also had to cover both worlds and
        scenarios — a distinction the manifest keeps (`world: true`) and a label
        would sooner or later get wrong.

        ⚠ Still true, and not contradicted by the group headings below: this is
        about naming the PAGE, and the page is still unnamed. A group heading
        names one shelf against another, which is the thing the reader cannot
        work out for themselves — and it appears only when there is another.
      */}
      <header className={styles.header}>
        <Wordmark variant="shelf" className={styles.mark} />
      </header>

      {tabbed && (
        /*
         * ⚠ MOBILE FIRST, AND THE BAR IS BUILT FOR THE NARROW CASE. Five shelves in Icelandic
         * (SAMRÆÐUR, VERALDIR) overflow a 375px screen, so the bar scrolls horizontally, bleeds
         * to the page edges — a tab cut off at the edge is what tells a reader to swipe — and
         * hides its scrollbar. `safe center` centres it when it fits and falls back to
         * flush-left when it does not, which is the one thing plain `center` gets wrong: it
         * clips the first tab out of reach.
         */
        <nav className={styles.tabs} role="tablist" onKeyDown={onTabKey}>
          {sections.map((s, i) => (
            <button
              key={s.key}
              type="button"
              role="tab"
              id={`tab-${s.key}`}
              aria-controls={`panel-${s.key}`}
              aria-selected={i === activeIndex}
              tabIndex={i === activeIndex ? 0 : -1}
              className={styles.tab}
              onClick={() => select(s.key)}
            >
              {t(HEADING[s.key])}
            </button>
          ))}
        </nav>
      )}

      {/* ⚠ `key` ON THE PANEL IS DELIBERATE: it remounts on every tab change, which replays the
          stagger. A shelf that arrives is the whole feel of this page, and a switched tab that
          simply appeared would read as a filter rather than as a shelf being set down. */}
      <section
        key={active.key}
        id={`panel-${active.key}`}
        className={styles.shelf}
        role={tabbed ? 'tabpanel' : undefined}
        aria-labelledby={tabbed ? `tab-${active.key}` : undefined}
      >
        {active.body}
      </section>

      <footer className={styles.footer}>
        {isAdmin && (
          <>
            <Link to="/admin" className={styles.quiet}>
              {t('shelf.theList')}
            </Link>
            {' · '}
            {/* Reading sittings back. Admin-only, and the endpoints behind it are checked
                server-side — see routes/admin/Transcripts.tsx. */}
            <Link to="/admin/transcripts" className={styles.quiet}>
              {t('shelf.sittings')}
            </Link>
            {' · '}
            {/* An instrument, admin-only, and not a price. See components/SeatPicker.tsx. */}
            <SeatPicker />
            {' · '}
          </>
        )}
        {/* The statement. On the shelf and nowhere inside a sitting — see App.tsx. */}
        {/* A setting, on the shelf and never inside a sitting — see ThemePicker. */}
        <ThemePicker />
        {' · '}
        <Link to="/wallet" className={styles.quiet}>
          {t('shelf.wallet')}
        </Link>
        {' · '}
        <button className={styles.quiet} onClick={() => void signOut()}>
          {t('shelf.signOut')}
        </button>
        {/*
          The second of the two places §5 names. On its own line rather than in the run of
          links: it is a setting, not a destination, and putting three language names in the
          same row as "sign out" reads as five things to click instead of two.
        */}
      </footer>
    </div>
  )
}
